import Box from '@mui/material/Box';

import {
  type SectionBackdropEmphasis,
  type SectionBackdropPlacement,
  sectionBackdropSx,
} from '@/theme';

import { AppImage, type Picture } from './AppImage';

export type SectionBackdropProps = {
  /** A cut-out on a transparent background, from `config/introVehicles`. */
  picture: Picture;
  /** Which bottom corner the car is anchored to. Defaults to the right. */
  placement?: SectionBackdropPlacement;
  /**
   * Set by `Section` from its own tone, not by the page: an `inverse` band is
   * near-black in both colour schemes and needs the quieter opacity in both.
   */
  onDarkBand?: boolean;
  /**
   * How loudly the car reads. Defaults to the watermark, so every band that
   * already wears one is untouched. `feature` is dark-band-only — see the
   * type's note in `theme/backdrops.ts`.
   */
  emphasis?: SectionBackdropEmphasis;
};

/**
 * The photographic half of a band's backdrop, and `SectionPattern`'s twin: it
 * takes a picture, paints the one arrangement `theme/backdrops.ts` knows, and
 * holds no state, no measurement and no lifecycle. Which band wears one is the
 * page's judgement; what one looks like is the theme's; this only puts the two
 * together.
 *
 * `aria-hidden` with an empty `alt` and `pointerEvents: none` because it is
 * decoration (§50): it is out of the accessibility tree, it is never announced
 * twice, and it never intercepts a click meant for the form stacked over it.
 * It is also never `priority` — a watermark at 11% must not compete with the
 * page's real LCP image for bandwidth.
 */
export function SectionBackdrop({
  picture,
  placement = 'right',
  onDarkBand = false,
  emphasis = 'watermark',
}: SectionBackdropProps) {
  // The slot the car is drawn into differs between the two strengths, so the
  // widths it offers the browser have to as well — a `feature` band asking
  // for the watermark's 46vw would hand a phone a rendition it then has to
  // scale up across the full width of the band.
  const sizes =
    emphasis === 'feature' ? '(min-width: 900px) 62vw, 100vw' : '(min-width: 900px) 46vw, 88vw';

  return (
    <Box aria-hidden sx={sectionBackdropSx(placement, onDarkBand, emphasis)}>
      <AppImage picture={picture} alt="" sizes={sizes} />
    </Box>
  );
}
